/**
 * El armazón de la aplicación: cabecera, navegación y barra inferior.
 *
 * ---------------------------------------------------------------------------
 *  Lo que hay en la cabecera, y por qué
 * ---------------------------------------------------------------------------
 *
 * · **El botón de registrar**, arriba y siempre a la vista. Es lo que más se hace en
 *   una aplicación de gastos, y tenerlo escondido en una pantalla obliga a navegar
 *   cada vez. En el móvil además va el botón redondo central de la barra inferior.
 *
 * · **El selector de período**, que manda sobre todas las pantallas a la vez. Está
 *   aquí y no dentro de cada pantalla para que Movimientos e Informes no puedan
 *   estar hablando de meses distintos sin que nadie se dé cuenta.
 *
 * · **El botón de tema**, que va cambiando claro → oscuro → sistema. Es lo que
 *   espera cualquiera que haya usado una aplicación, y estaba en Ajustes sin hacer
 *   nada.
 *
 * · **Los iconos**, uno distinto por sección, en el escritorio y en el móvil. Antes
 *   el escritorio no tenía ninguno y en el móvil ocho secciones compartían el mismo
 *   icono de barras, que no distingue nada.
 */

import {
  ArrowLeftRight,
  Bot,
  ChartPie,
  Eye,
  EyeOff,
  FileUp,
  House,
  Landmark,
  LogOut,
  Menu,
  Monitor,
  Moon,
  NotebookPen,
  PiggyBank,
  Plus,
  Receipt,
  Repeat,
  Settings,
  Sun,
  Target,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useSession } from '../lib/session';
import { THEME_LABEL, useTheme } from '../lib/theme';
import { OfflineBar } from './OfflineBar';
import { PeriodPicker } from './PeriodPicker';
import { SectionExport } from './SectionExport';

interface NavItem {
  to: string;
  label: string;
  /** El icono. Uno distinto por sección: es lo que permite reconocerla de un vistazo. */
  icon: typeof House;
}

/**
 * Las cuatro secciones principales. Son las que caben en la barra inferior del móvil
 * junto al botón de registrar.
 */
const MAIN_NAV: readonly NavItem[] = [
  { to: '/', label: 'Inicio', icon: House },
  { to: '/movimientos', label: 'Movimientos', icon: ArrowLeftRight },
  { to: '/fiado', label: 'Fiado', icon: NotebookPen },
  { to: '/deudas', label: 'Deudas', icon: Landmark },
];

/** El resto. Cada una con su icono, que antes compartían todos el mismo. */
const MORE_NAV: readonly NavItem[] = [
  { to: '/presupuestos', label: 'Presupuestos', icon: PiggyBank },
  { to: '/metas', label: 'Metas de ahorro', icon: Target },
  { to: '/recurrentes', label: 'Gastos fijos', icon: Repeat },
  { to: '/compartidos', label: 'Gastos compartidos', icon: Users },
  { to: '/recibos', label: 'Recibos', icon: Receipt },
  { to: '/importar', label: 'Importar extractos', icon: FileUp },
  { to: '/informes', label: 'Informes', icon: ChartPie },
  { to: '/copiloto', label: 'Copiloto', icon: Bot },
  { to: '/ajustes', label: 'Ajustes', icon: Settings },
];

const ALL_NAV: readonly NavItem[] = [...MAIN_NAV, ...MORE_NAV];

/** El icono que le toca al tema actual. */
const THEME_ICON = { LIGHT: Sun, DARK: Moon, SYSTEM: Monitor } as const;

export function AppShell(): React.JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const user = useSession((s) => s.user);
  const stealth = useSession((s) => s.stealth);
  const toggleStealth = useSession((s) => s.toggleStealth);
  const signOut = useSession((s) => s.signOut);
  const { preference, cycle } = useTheme();
  const ThemeIcon = THEME_ICON[preference];

  // El selector de período no se enseña en Ajustes: ahí no hay nada que filtrar.
  const showPeriod = location.pathname !== '/ajustes';

  // Al cambiar de pantalla se cierra el menú lateral y se sube al principio.
  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  // Se busca el nombre de la sección actual para el título del móvil.
  const current = ALL_NAV.find((item) =>
    item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to),
  );

  const themeLabel = `Tema: ${THEME_LABEL[preference]}. Toca para cambiar.`;

  return (
    <div className="min-h-[100dvh] pb-24 sm:pb-0">
      <header className="ed-safe-top sticky top-0 z-30 border-b border-ed-bg-border/30 bg-ed-bg-primary/85 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-2 px-3 pb-2 sm:px-4">
          <button
            type="button"
            className="ed-btn-ghost !min-h-[40px] !px-2"
            aria-label={menuOpen ? 'Cerrar el menú' : 'Abrir el menú'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>

          <NavLink to="/" className="flex min-w-0 items-center gap-2" aria-label="Ir al inicio">
            <img
              src="/android-chrome-192x192.png"
              alt=""
              width={30}
              height={30}
              className="h-[30px] w-[30px] shrink-0 rounded-lg"
            />
            <span className="hidden font-display text-base font-bold tracking-tight sm:inline">
              El<span className="text-ed-gold">Descuadre</span>
            </span>
            {/* En el móvil se enseña la sección actual, que es más útil que el nombre. */}
            <span className="truncate font-display text-sm font-semibold sm:hidden">
              {current?.label ?? 'ElDescuadre'}
            </span>
          </NavLink>

          <div className="ml-auto flex items-center gap-1">
            {showPeriod && (
              <div className="hidden sm:block">
                <PeriodPicker />
              </div>
            )}

            {/* Exportar la sección en la que estás, con su período. Va aquí y no en
                cada pantalla para que no falte en ninguna. */}
            <SectionExport />

            {/* Registrar: lo que más se hace, siempre a la vista. */}
            <NavLink
              to="/nuevo"
              className="ed-btn-primary !min-h-[40px] !px-3 hidden text-sm sm:inline-flex"
            >
              <Plus size={16} aria-hidden="true" />
              Registrar
            </NavLink>

            <button
              type="button"
              className="ed-btn-ghost !min-h-[40px] !px-2"
              aria-label={themeLabel}
              title={themeLabel}
              onClick={() => cycle()}
            >
              <ThemeIcon size={20} aria-hidden="true" />
            </button>

            <button
              type="button"
              className="ed-btn-ghost !min-h-[40px] !px-2"
              aria-label={stealth ? 'Mostrar los importes' : 'Ocultar los importes'}
              aria-pressed={stealth}
              title={stealth ? 'Mostrar los importes' : 'Ocultar los importes'}
              onClick={toggleStealth}
            >
              {stealth ? (
                <EyeOff size={20} aria-hidden="true" />
              ) : (
                <Eye size={20} aria-hidden="true" />
              )}
            </button>

            <button
              type="button"
              className="ed-btn-ghost !min-h-[40px] !px-2"
              aria-label="Cerrar sesión"
              title="Cerrar sesión"
              onClick={() => void signOut()}
            >
              <LogOut size={20} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* El período, en su propia fila en el móvil, donde no cabe arriba. */}
        {showPeriod && (
          <div className="mx-auto flex max-w-5xl justify-center px-3 pb-2 sm:hidden">
            <PeriodPicker />
          </div>
        )}

        {/* Navegación de escritorio, con icono en cada sección. */}
        <nav className="mx-auto hidden max-w-5xl gap-0.5 px-4 pb-2 sm:flex" aria-label="Principal">
          {ALL_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-ed-gold/15 text-ed-gold'
                    : 'text-ed-text-muted hover:bg-ed-bg-elevated/50 hover:text-ed-text-white'
                }`
              }
            >
              <item.icon size={16} aria-hidden="true" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      {/* Estado de la conexión. Solo aparece cuando hay algo que contar. */}
      <OfflineBar />

      {/* Menú lateral: en el móvil lleva a todo, y en el escritorio no se usa. */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 sm:hidden">
          <div
            className="absolute inset-0"
            style={{ background: 'var(--ed-backdrop)' }}
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <nav
            className="ed-safe-top absolute left-0 top-0 h-full w-[80%] max-w-xs overflow-y-auto border-r border-ed-bg-border/40 bg-ed-bg-primary px-4 pb-8"
            aria-label="Todas las secciones"
          >
            <p className="mb-3 mt-2 truncate text-xs uppercase tracking-wider text-ed-text-muted">
              {user?.displayName ?? 'Tu cuenta'}
            </p>
            <ul className="space-y-1">
              {ALL_NAV.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      `flex min-h-[46px] items-center gap-3 rounded-ed px-3 text-sm font-medium ${
                        isActive
                          ? 'bg-ed-gold/15 text-ed-gold'
                          : 'text-ed-text-silver hover:bg-ed-bg-elevated/50'
                      }`
                    }
                  >
                    <item.icon size={19} aria-hidden="true" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}

      <main className="mx-auto max-w-5xl px-3 py-4 sm:px-4">
        <Outlet />
      </main>

      {/* Barra inferior del móvil, con el botón central de registrar. */}
      <nav
        className="ed-safe-bottom fixed inset-x-0 bottom-0 z-30 border-t border-ed-bg-border/30 bg-ed-bg-primary/95 backdrop-blur sm:hidden"
        aria-label="Principal"
      >
        <ul className="mx-auto grid max-w-md grid-cols-5">
          {MAIN_NAV.slice(0, 2).map((item) => (
            <BottomNavLink key={item.to} item={item} />
          ))}

          <li className="flex items-center justify-center">
            <NavLink
              to="/nuevo"
              className="-mt-6 flex h-14 w-14 items-center justify-center rounded-full bg-ed-gold-imperial shadow-ed-glow-gold"
              style={{ color: 'rgb(var(--ed-on-gold-rgb))' }}
              aria-label="Registrar un movimiento"
            >
              <Plus size={26} aria-hidden="true" />
            </NavLink>
          </li>

          {MAIN_NAV.slice(2).map((item) => (
            <BottomNavLink key={item.to} item={item} />
          ))}
        </ul>
      </nav>
    </div>
  );
}

function BottomNavLink({ item }: { item: NavItem }): React.JSX.Element {
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.to === '/'}
        className={({ isActive }) =>
          `flex min-h-[56px] flex-col items-center justify-center gap-0.5 px-1 text-[10px] font-medium ${
            isActive ? 'text-ed-gold' : 'text-ed-text-muted'
          }`
        }
      >
        <item.icon size={20} aria-hidden="true" />
        {item.label}
      </NavLink>
    </li>
  );
}
